import React, { useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, Graticule } from "react-simple-maps";
import { toast } from "react-toastify";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Harmonize common country-name variants
const nameMap = {
    "United States of America": "United States",
    "Russian Federation": "Russia",
    "Korea, Republic of": "South Korea",
    "Korea, Democratic People's Republic of": "North Korea",
    Czechia: "Czech Republic",
    "Viet Nam": "Vietnam",
    "United Republic of Tanzania": "Tanzania",
    "Democratic Republic of the Congo": "DR Congo",
    Congo: "Republic of the Congo",
    Eswatini: "Swaziland",
    "Lao People's Democratic Republic": "Laos",
    "Syrian Arab Republic": "Syria",
    "Iran, Islamic Republic of": "Iran",
    "Moldova, Republic of": "Moldova",
    "Bolivia, Plurinational State of": "Bolivia",
    "Venezuela, Bolivarian Republic of": "Venezuela",
    "United Kingdom": "United Kingdom",
};

export default function CountryMap({
    countryCounts, // { CountryName: count }
    selectedCountryName, // string|null
    onSelectCountry, // (name|null) => void
}) {
    const wrapRef = useRef(null);
    const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, text: "" });

    const maxCount = useMemo(() => {
        const vals = Object.values(countryCounts || {});
        return vals.length ? Math.max(...vals) : 0;
    }, [countryCounts]);

    const harmonise = (n) => nameMap[n] || n;

    // Match MonthChart palette (slate-900 in RGBA)
    const CHART_RGB = "15, 23, 42"; // #0f172a (slate-900)
    const BASE_ALPHA = 0.10; // light tint for low counts
    const MAX_ALPHA = 0.85;  // MonthChart bar alpha

    const fillFor = (cnt) => {
        if (!cnt || maxCount === 0) return "rgb(241, 245, 249)"; // slate-100
        const t = Math.sqrt(cnt / maxCount); // 0..1
        const a = BASE_ALPHA + t * (MAX_ALPHA - BASE_ALPHA);
        return `rgba(${CHART_RGB}, ${a})`;
    };

    const handleMove = (e, text) => {
        const el = wrapRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setTooltip({
            show: true,
            x: e.clientX - rect.left + 12,
            y: e.clientY - rect.top + 12,
            text,
        });
    };

    const isSelected = (name) =>
        selectedCountryName && selectedCountryName.toLowerCase() === name.toLowerCase();

    return (
        <div className="w-full max-w-full">
            <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">World map</div>
                        <div className="text-xs text-slate-600">
                            Click a country to toggle a filter. Hover to preview counts.
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                            {maxCount ? `Max: ${maxCount}` : "No data"}
                        </span>
                        {selectedCountryName ? (
                            <span
                                className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-900"
                                title={selectedCountryName}
                            >
                                {selectedCountryName}
                            </span>
                        ) : (
                            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                                No country selected
                            </span>
                        )}
                    </div>
                </div>

                <div
                    ref={wrapRef}
                    className="relative mt-3 w-full max-w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50/50 aspect-[21/10] min-h-[360px]"
                >
                    <ComposableMap
                        projection="geoNaturalEarth1"
                        projectionConfig={{ scale: 165, center: [0, 0] }}
                        style={{ width: "100%", height: "100%" }}
                        preserveAspectRatio="xMidYMin meet"
                    >
                        <Graticule stroke="#94a3b8" strokeOpacity={0.15} strokeWidth={0.5} />
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) =>
                                geographies.map((geo) => {
                                    const raw = geo.properties.name;
                                    const name = harmonise(raw);
                                    const count = countryCounts?.[name] || 0;

                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            role="button"
                                            aria-label={`${name}: ${count}`}
                                            onClick={() => {
                                                const next = isSelected(name) ? null : name;
                                                onSelectCountry(next);
                                                if (next)
                                                    toast.info(
                                                        `Country: ${name} • ${count} lead${count === 1 ? "" : "s"}`
                                                    );
                                                else toast.info("Cleared country filter");
                                            }}
                                            onMouseMove={(e) => handleMove(e, `${name}: ${count}`)}
                                            onMouseLeave={() => setTooltip({ show: false, x: 0, y: 0, text: "" })}
                                            style={{
                                                default: {
                                                    fill: fillFor(count),
                                                    outline: "none",
                                                    stroke: isSelected(name) ? "#0f172a" : "#ffffff",
                                                    strokeWidth: isSelected(name) ? 1.25 : 0.5,
                                                    transition: "fill 150ms ease-out, stroke 150ms ease-out",
                                                    cursor: "pointer",
                                                    strokeLinejoin: "round",
                                                },
                                                hover: {
                                                    fill: count
                                                        ? `rgba(${CHART_RGB}, 0.95)` // MonthChart hover
                                                        : "rgb(203, 213, 225)",      // slate-300 for no-data hover
                                                    outline: "none",
                                                },
                                                pressed: {
                                                    fill: `rgba(${CHART_RGB}, 0.98)`,
                                                    outline: "none",
                                                },
                                            }}
                                        />
                                    );
                                })
                            }
                        </Geographies>
                    </ComposableMap>

                    {tooltip.show && (
                        <div
                            className="absolute z-10 pointer-events-none rounded-md bg-slate-900/95 px-2 py-1 text-xs text-white shadow-lg ring-1 ring-black/10"
                            style={{ left: tooltip.x, top: tooltip.y }}
                        >
                            {tooltip.text}
                        </div>
                    )}

                    {/* Legend (matches MonthChart palette) */}
                    <div className="absolute right-3 bottom-3 rounded-lg bg-white/90 backdrop-blur px-3 py-2 shadow-sm border border-slate-200">
                        <div className="text-[11px] font-semibold text-slate-800 mb-1">Leads by country</div>
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] text-slate-600">Low</span>
                            <div
                                className="h-2 w-28 rounded"
                                style={{
                                    background: `linear-gradient(90deg, rgba(${CHART_RGB}, ${BASE_ALPHA}) 0%, rgba(${CHART_RGB}, ${MAX_ALPHA}) 100%)`,
                                }}
                            />
                            <span className="text-[11px] text-slate-600">High</span>
                        </div>
                    </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-3 text-xs sm:text-sm text-slate-600">
                    <div className="truncate">Map projection: Natural Earth (visual straightening).</div>
                    <div className="shrink-0 hidden sm:block">Tip: Click again to clear.</div>
                </div>
            </div>
        </div>
    );
}
